// ─── Confetti ────────────────────────────────────────────────────────────
// Lightweight canvas confetti. Plays whenever a threshold (free shipping or
// free gift) newly becomes met while the drawer stays open (e.g. changing a
// line item quantity), and also every time the drawer is opened while a
// threshold is currently met (e.g. add to cart, or reopening the drawer
// later). Bursts outward from each [data-confetti-origin] text line instead
// of raining from the top. No external dependency: a handful of rects with
// basic gravity is enough for a celebratory cue and keeps the JS payload
// small.

const CONFETTI_START_DELAY = 500;
const CONFETTI_SPEED_FACTOR = 1.2; // ~20% faster particle motion
const CONFETTI_DURATION = 1500; // 1800ms / 1.2, so the burst also finishes ~20% sooner

function createConfettiBurst(origin, colors) {
  const particles = [];
  for (let i = 0; i < 65; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = (2 + Math.random() * 4) * CONFETTI_SPEED_FACTOR;
    particles.push({
      x: origin.x,
      y: origin.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 2 * CONFETTI_SPEED_FACTOR,
      size: 4 + Math.random() * 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      spin: -8 + Math.random() * 16,
    });
  }
  return particles;
}

function launchCartConfetti(canvas) {
  if (!canvas || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const container = canvas.parentElement;

  setTimeout(() => {
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const origins = Array.from(container.querySelectorAll('[data-confetti-origin]')).map((el) => {
      const elRect = el.getBoundingClientRect();
      return {
        x: elRect.left + elRect.width / 2 - rect.left,
        y: elRect.top + elRect.height / 2 - rect.top,
      };
    });
    if (!origins.length) return;

    const ctx = canvas.getContext('2d');
    const rootStyles = getComputedStyle(document.documentElement);
    const colors = [
      rootStyles.getPropertyValue('--color-primary').trim() || '#bfa15a',
      rootStyles.getPropertyValue('--color-secondary').trim() || '#111111',
      '#ffffff',
    ];

    const particles = origins.flatMap((origin) => createConfettiBurst(origin, colors));
    const gravity = 0.12 * CONFETTI_SPEED_FACTOR;
    const start = performance.now();

    function frame(now) {
      const elapsed = now - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.vy += gravity;
        particle.vx *= 0.98;
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.rotation += particle.spin;

        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate((particle.rotation * Math.PI) / 180);
        ctx.globalAlpha = Math.max(0, 1 - elapsed / CONFETTI_DURATION);
        ctx.fillStyle = particle.color;
        ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size * 0.6);
        ctx.restore();
      });

      if (elapsed < CONFETTI_DURATION) {
        requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }

    requestAnimationFrame(frame);
  }, CONFETTI_START_DELAY);
}

// Snapshot which thresholds are currently shown as unlocked, so a caller can
// diff before/after a cart update and only celebrate genuine transitions
// (not every drawer open, and not re-celebrating a threshold that was
// already unlocked).
function getUnlockedThresholds() {
  return {
    freeShipping: !!document.querySelector('[data-confetti-origin="free-shipping"]'),
    freeGift: !!document.querySelector('[data-confetti-origin="free-gift"]'),
  };
}

function hasNewlyUnlockedThreshold(before, after) {
  return (!before.freeShipping && after.freeShipping) || (!before.freeGift && after.freeGift);
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) {
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.dataset.open === 'true') {
        this.close();
      }
    });
  }

  open() {
    this.dataset.open = 'true';
    const unlocked = getUnlockedThresholds();
    if (unlocked.freeShipping || unlocked.freeGift) {
      launchCartConfetti(this.querySelector('[data-cart-confetti]'));
    }
  }

  celebrate() {
    launchCartConfetti(this.querySelector('[data-cart-confetti]'));
  }

  close() {
    this.dataset.open = 'false';
  }

  renderSection(html) {
    if (!html) return;
    const newDoc = new DOMParser().parseFromString(html, 'text/html');

    const inner = this.querySelector('[data-cart-drawer-inner]');
    const newInner = newDoc.querySelector('[data-cart-drawer-inner]');
    if (inner && newInner) {
      inner.innerHTML = newInner.innerHTML;
      // innerHTML-injected <script> tags never execute, so clone-and-replace
      // each one to re-run slider init scripts (e.g. the free gift slider).
      inner.querySelectorAll('script').forEach((oldScript) => {
        const newScript = document.createElement('script');
        for (const attr of oldScript.attributes) {
          newScript.setAttribute(attr.name, attr.value);
        }
        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
      });
    }

    // The recommendations panel excludes products already in the cart, and
    // hides itself entirely once the cart is empty, so both its content and
    // its `hidden` state must be re-synced on every cart change, not just on open.
    const recommendationsPanel = this.querySelector('[data-cart-recommendations]');
    const newRecommendationsPanel = newDoc.querySelector('[data-cart-recommendations]');
    if (recommendationsPanel && newRecommendationsPanel) {
      recommendationsPanel.hidden = newRecommendationsPanel.hidden;
      const recommendations = recommendationsPanel.querySelector('[data-cart-recommendations-inner]');
      const newRecommendations = newRecommendationsPanel.querySelector('[data-cart-recommendations-inner]');
      if (recommendations && newRecommendations) {
        recommendations.innerHTML = newRecommendations.innerHTML;
      }
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

function getCartDrawer() {
  return document.querySelector('cart-drawer');
}

// The /cart page renders the same `cart-drawer-items` markup as the drawer
// (see sections/cart.liquid), so its AJAX updates are kept in sync here too.
function getCartPageInner() {
  return document.querySelector('[data-cart-page-inner]');
}

// The drawer lives in the `header-group` section group, so its real section ID
// is prefixed (e.g. `sections--123__cart-drawer`). Requesting the bare file name
// makes Shopify render the section with its *schema defaults* instead of the
// merchant's settings, which silently drops the free gift picker and the free
// shipping bar from the AJAX response.
function getCartDrawerSectionId() {
  return getCartDrawer()?.dataset.sectionId || 'cart-drawer';
}

function getCartPageSectionId() {
  return getCartPageInner()?.dataset.sectionId;
}

function getCartSectionIds() {
  return [getCartDrawerSectionId(), getCartPageSectionId()].filter(Boolean).join(',');
}

function renderCartPage(html) {
  const pageInner = getCartPageInner();
  if (!pageInner || !html) return;

  const newDoc = new DOMParser().parseFromString(html, 'text/html');
  const newPageInner = newDoc.querySelector('[data-cart-page-inner]');
  if (!newPageInner) return;

  pageInner.innerHTML = newPageInner.innerHTML;
  // innerHTML-injected <script> tags never execute, so clone-and-replace
  // each one to re-run slider init scripts (e.g. the free gift slider).
  pageInner.querySelectorAll('script').forEach((oldScript) => {
    const newScript = document.createElement('script');
    for (const attr of oldScript.attributes) {
      newScript.setAttribute(attr.name, attr.value);
    }
    newScript.textContent = oldScript.textContent;
    oldScript.replaceWith(newScript);
  });
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = count === 0;
  });

  document.querySelectorAll('[data-cart-count-inline]').forEach((el) => {
    el.textContent = count;
  });
}

function refreshCart(data) {
  const sections = data.sections || {};
  getCartDrawer()?.renderSection(sections[getCartDrawerSectionId()]);
  renderCartPage(sections[getCartPageSectionId()]);
  updateCartCount(data.item_count);
  window.RasasiDeliveryLocation?.render();
  window.RasasiLoadSwiper?.();
  window.RasasiGiftBox?.sync();
  ensureDefaultPaymentChoice();
}

// Shared with assets/cart-gift-box.js so it can add/refresh the cart without
// duplicating the section-id lookup and drawer re-render logic here.
window.RasasiCart = {
  refresh: refreshCart,
  getSectionIds: getCartSectionIds,
  openDrawer: () => getCartDrawer()?.open(),
};

let paymentChoiceSaving = false;

function ensureDefaultPaymentChoice() {
  const selector = document.querySelector('[data-prepaid-selector][data-needs-default="true"]');
  if (!selector || paymentChoiceSaving) return;
  selector.querySelector('input[value="prepaid"]').dispatchEvent(new Event('change', { bubbles: true }));
}

document.addEventListener('change', async (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-prepaid-selector] input[type="radio"]') || paymentChoiceSaving) return;

  const selector = input.closest('[data-prepaid-selector]');
  const previous = selector.dataset.current;
  const needsDefault = selector.dataset.needsDefault === 'true';
  paymentChoiceSaving = true;
  selector.querySelector('[data-prepaid-retry]').hidden = true;
  document.querySelectorAll('[data-prepaid-selector] fieldset, [data-cart-checkout] [name="checkout"]').forEach((element) => {
    element.disabled = true;
  });
  selector.querySelector('[data-prepaid-status]').textContent = selector.dataset.savingText;

  try {
    const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: { prepaid_discount_choice: input.value },
        sections: getCartSectionIds(),
      }),
    });
    if (!response.ok) throw new Error('Cart update failed');
    const data = await response.json();
    if (data.attributes?.prepaid_discount_choice !== input.value) throw new Error('Cart choice was not saved');

    if (!data.sections?.[getCartDrawerSectionId()] || (getCartPageSectionId() && !data.sections[getCartPageSectionId()])) {
      window.location.reload();
      return;
    }
    refreshCart(data);
  } catch (_error) {
    try {
      const cartResponse = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Unable to confirm cart choice');
      const currentCart = await cartResponse.json();
      const savedChoice = currentCart.attributes?.prepaid_discount_choice;
      if (savedChoice === input.value || (savedChoice && savedChoice !== previous)) {
        window.location.reload();
        return;
      }
    } catch (_readError) {
      if (!needsDefault) {
        window.location.reload();
        return;
      }
    }
    document.querySelectorAll('[data-prepaid-selector]').forEach((currentSelector) => {
      currentSelector.querySelector(`input[value="${previous}"]`).checked = true;
    });
    selector.querySelector('[data-prepaid-status]').textContent = selector.dataset.errorText;
    if (needsDefault) selector.querySelector('[data-prepaid-retry]').hidden = false;
  } finally {
    paymentChoiceSaving = false;
    document.querySelectorAll('[data-prepaid-selector] fieldset, [data-cart-checkout] [name="checkout"]').forEach((element) => {
      element.disabled = element.name === 'checkout' && !!document.querySelector('[data-prepaid-selector][data-needs-default="true"]');
    });
  }
});

document.addEventListener('click', (event) => {
  const retry = event.target.closest('[data-prepaid-retry]');
  if (!retry || paymentChoiceSaving) return;
  retry.hidden = true;
  retry.closest('[data-prepaid-selector]').querySelector('input[value="prepaid"]').dispatchEvent(new Event('change', { bubbles: true }));
});

ensureDefaultPaymentChoice();

// ─── Free gift (Discovery Set) ──────────────────────────────────────────────
// The gift line item is identified purely by the cart line-item property
// `_is_free_gift`; a Shopify automatic discount ("Buy X Get Y") is what
// actually makes that line free at checkout.

function getFreeGiftPicker() {
  return document.querySelector('[data-free-gift-picker]');
}

function getFreeGiftThreshold() {
  const raw = parseFloat(getFreeGiftPicker()?.dataset.freeGiftThreshold);
  return Number.isFinite(raw) ? raw : 0;
}

function computeFreeGiftQualifyingTotal(cart) {
  const existingGift = cart.items.find((item) => item.properties && item.properties._is_free_gift === 'true') || null;

  let qualifyingCents = Number(cart.total_price) || 0;
  if (existingGift) {
    qualifyingCents -= Number(existingGift.final_line_price) || 0;
  }
  if (qualifyingCents < 0) qualifyingCents = 0;

  return { qualifyingTotal: qualifyingCents / 100, existingGift };
}

let selectingFreeGift = false;

async function selectFreeGift(variantId) {
  if (selectingFreeGift || !variantId) return;
  selectingFreeGift = true;

  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
    const { qualifyingTotal, existingGift } = computeFreeGiftQualifyingTotal(cart);

    if (qualifyingTotal < getFreeGiftThreshold()) {
      alert(getFreeGiftPicker()?.dataset.freeGiftNotEligible || 'Add more items to qualify for a free gift.');
      refreshCart(cart);
      return;
    }

    if (existingGift) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: existingGift.key, quantity: 0 }),
      });
    }

    const data = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _is_free_gift: 'true' } }],
        sections: getCartSectionIds(),
      }),
    }).then((response) => response.json());

    // /cart/add.js only returns the added line items, not the full cart, so
    // it has no item_count; fetch the cart for the up-to-date count.
    const updatedCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) =>
      response.json()
    );
    refreshCart({ ...updatedCart, sections: data.sections });
  } catch (error) {
    alert(error.message || 'Failed to add free gift');
  } finally {
    selectingFreeGift = false;
  }
}

async function removeFreeGift(lineItemKey) {
  const data = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: lineItemKey, quantity: 0, sections: getCartSectionIds() }),
  }).then((response) => response.json());

  refreshCart(data);
}

async function checkFreeGiftEligibility() {
  if (!getFreeGiftPicker()) return;

  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
  const { qualifyingTotal, existingGift } = computeFreeGiftQualifyingTotal(cart);

  if (existingGift && qualifyingTotal < getFreeGiftThreshold()) {
    await removeFreeGift(existingGift.key);
  }
}

window.selectFreeGift = selectFreeGift;
window.removeFreeGift = removeFreeGift;

async function normalizeLimitedProduct(data) {
  const addedItem = data.items ? data.items[0] : data;
  const productId = addedItem.product_id;
  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
  const productItems = cart.items.filter((item) => item.product_id === productId);
  const productQuantity = productItems.reduce((total, item) => total + item.quantity, 0);

  if (productQuantity <= 1) {
    return { ...cart, sections: data.sections };
  }

  const updates = {};
  productItems.forEach((item, index) => {
    updates[item.key] = index === 0 ? 1 : 0;
  });

  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates, sections: getCartSectionIds() }),
  }).then((response) => response.json());
}

document.addEventListener('submit', async (event) => {
  const checkoutForm = event.target.closest('[data-cart-checkout]');
  if (checkoutForm) {
    if (paymentChoiceSaving || document.querySelector('[data-prepaid-selector][data-needs-default="true"]')) {
      event.preventDefault();
      ensureDefaultPaymentChoice();
      return;
    }
    if (checkoutForm.dataset.checkoutValidated === 'true') {
      delete checkoutForm.dataset.checkoutValidated;
      return;
    }

    event.preventDefault();
    const checkoutButton = event.submitter || checkoutForm.querySelector('[name="checkout"]');
    if (checkoutButton) {
      checkoutButton.disabled = true;
      checkoutButton.setAttribute('data-loading', 'true');
    }

    try {
      const validationUrl = new URL(checkoutForm.action);
      validationUrl.searchParams.set('section_id', 'cart');
      const validationHtml = await fetch(validationUrl, { headers: { Accept: 'text/html' } }).then((response) => {
        if (!response.ok) throw new Error('Unable to validate cart');
        return response.text();
      });
      const validationDocument = new DOMParser().parseFromString(validationHtml, 'text/html');
      const currentCheckoutForm = validationDocument.querySelector('[data-cart-checkout]');
      if (!currentCheckoutForm) throw new Error('Unable to read cart validation');

      if (currentCheckoutForm.dataset.cartQuantityValid !== 'true') {
        const clearedCart = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ sections: getCartSectionIds() }),
        }).then((response) => response.json());
        refreshCart(clearedCart);
        return;
      }

      checkoutForm.dataset.checkoutValidated = 'true';
      if (checkoutButton) {
        checkoutButton.disabled = false;
        checkoutButton.removeAttribute('data-loading');
      }
      checkoutForm.requestSubmit(checkoutButton);
    } catch (error) {
      console.error(error);
    } finally {
      if (checkoutButton) {
        checkoutButton.disabled = false;
        checkoutButton.removeAttribute('data-loading');
      }
    }
    return;
  }

  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form) return;

  event.preventDefault();
  const submitButton = form.querySelector('[type="submit"]');
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.setAttribute('data-loading', 'true');
  }

  const formData = new FormData(form);
  formData.append('sections', getCartSectionIds());

  try {
    const response = await fetch(form.action, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const data = await response.json();

    if (data.status) {
      console.error(data.description || data.message);
      return;
    }

    const cart = form.querySelector('[data-quantity-limit="1"]')
      ? await normalizeLimitedProduct(data)
      : await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((cartResponse) =>
          cartResponse.json()
        );
    refreshCart(form.querySelector('[data-quantity-limit="1"]') ? cart : { ...cart, sections: data.sections });
    getCartDrawer()?.open();
  } finally {
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.removeAttribute('data-loading');
    }
  }
});

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-cart-drawer-toggle]')) {
    event.preventDefault();
    getCartDrawer()?.open();
    return;
  }

  const item = event.target.closest('[data-cart-item]');
  if (!item) return;

  const quantityEl = item.querySelector('[data-cart-quantity-value]');
  if (!quantityEl) return;
  const currentQuantity = parseInt(quantityEl.textContent, 10);
  let quantity;
  let loadingTarget;

  const removeButton = event.target.closest('[data-cart-item-remove]');

  if (event.target.closest('[data-cart-quantity-increase]')) {
    quantity = currentQuantity + 1;
    loadingTarget = item.querySelector('[data-cart-quantity-selector]');
  } else if (event.target.closest('[data-cart-quantity-decrease]')) {
    quantity = Math.max(0, currentQuantity - 1);
    loadingTarget = item.querySelector('[data-cart-quantity-selector]');
  } else if (removeButton) {
    quantity = 0;
    loadingTarget = removeButton;
  } else {
    return;
  }

  loadingTarget.dataset.loading = 'true';
  const beforeUnlocked = getUnlockedThresholds();

  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: item.dataset.cartItemKey, quantity, sections: getCartSectionIds() }),
  })
    .then((response) => response.json())
    .then((data) => {
      refreshCart(data);
      checkFreeGiftEligibility();
      if (hasNewlyUnlockedThreshold(beforeUnlocked, getUnlockedThresholds())) {
        getCartDrawer()?.celebrate();
      }
    });
});
